import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FileText, Copy, Check, RefreshCw } from 'lucide-react';
import { GlassButton, GlassCard } from '../ui';
import { getCustomDomainSeoFilePreview, SeoCrawlerFileKey, SeoFilePreview } from '../../services/api';

interface Props {
    domainId: string;
    domain: string;
}

const FILE_TABS: { key: SeoCrawlerFileKey; label: string }[] = [
    { key: 'robots', label: 'robots.txt' },
    { key: 'sitemap', label: 'sitemap.xml' },
    { key: 'llms', label: 'llms.txt' },
    { key: 'llms-full', label: 'llms-full.txt' },
];

const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 12px',
    borderRadius: 6,
    border: '1px solid var(--stroke)',
    background: active ? 'var(--brand-primary)' : 'var(--panel-strong)',
    // var(...) with a hex fallback, not a bare hex literal — matches the established
    // var(--danger, #dc2626) convention (code review I9).
    color: active ? 'var(--text-on-brand, #fff)' : 'var(--text)',
    // Non-shorthand equivalent of `font: 'inherit'` — a `font` shorthand applied after
    // fontSize/fontWeight would silently reset both back to inherited UA defaults (code
    // review I11). We only need to stop inheriting the UA default font-family.
    fontFamily: 'inherit',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
});

// Read-only preview of the four per-domain generated crawler files. Rendered
// inside AimeHostedConnectPanel only when an active CustomDomain exists — see
// specs/completed/sitemap-llms-seo-metadata.md Phase 5.
const SeoFilesPanel: React.FC<Props> = ({ domainId, domain }) => {
    const [activeFile, setActiveFile] = useState<SeoCrawlerFileKey>('robots');
    const [preview, setPreview]       = useState<SeoFilePreview | null>(null);
    const [loading, setLoading]       = useState(true);
    const [error, setError]           = useState<string | null>(null);
    const [copied, setCopied]         = useState(false);
    // Guards against fast tab-switching landing an older response last (code review I9): each
    // load() call stamps the in-flight request with a token; only the load whose token still
    // matches the latest one when its response arrives is allowed to write state.
    const requestSeq = useRef(0);

    const load = useCallback(async (file: SeoCrawlerFileKey) => {
        const seq = ++requestSeq.current;
        setLoading(true);
        setError(null);
        try {
            const res = await getCustomDomainSeoFilePreview(domainId, file);
            if (seq !== requestSeq.current) return;  // a newer request has since started
            setPreview(res);
        } catch (e: any) {
            if (seq !== requestSeq.current) return;
            setError(e?.response?.data?.detail || 'Failed to load preview');
            setPreview(null);
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
    }, [domainId]);

    useEffect(() => {
        load(activeFile);
    }, [activeFile, load]);

    const handleCopy = async () => {
        if (!preview) return;
        try {
            await navigator.clipboard.writeText(preview.content);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* clipboard unavailable — non-fatal */
        }
    };

    return (
        <GlassCard style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <FileText size={18} style={{ color: 'var(--primary-500)' }} />
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text)' }}>Crawler Files</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                        What search engines and LLM crawlers will see at https://{domain}
                    </div>
                </div>
                <GlassButton variant="secondary" onClick={() => load(activeFile)} disabled={loading} style={{ padding: '4px 8px' }}>
                    <RefreshCw size={13} />
                </GlassButton>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} role="tablist" aria-label="Crawler file">
                {FILE_TABS.map(tab => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={activeFile === tab.key}
                        style={tabStyle(activeFile === tab.key)}
                        onClick={() => setActiveFile(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {error && (
                <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, color: 'var(--danger, #dc2626)', fontSize: '0.85rem' }}>
                    {error}
                </div>
            )}

            {!error && (
                <>
                    <div style={{ position: 'relative' }}>
                        <pre
                            style={{
                                margin: 0,
                                padding: 14,
                                background: 'var(--panel-strong)',
                                border: '1px solid var(--stroke)',
                                borderRadius: 8,
                                color: 'var(--text)',
                                fontSize: '0.78rem',
                                lineHeight: 1.5,
                                maxHeight: 320,
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}
                        >
                            {loading ? 'Loading…' : (preview?.content || '(empty)')}
                        </pre>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            {preview && !loading && (
                                <>
                                    {preview.byte_size.toLocaleString()} bytes
                                    {preview.url_count > 0 && ` · ${preview.url_count} URL${preview.url_count === 1 ? '' : 's'}`}
                                </>
                            )}
                        </div>
                        <GlassButton variant="secondary" onClick={handleCopy} disabled={loading || !preview}>
                            {copied ? <Check size={13} /> : <Copy size={13} />}
                            {copied ? 'Copied' : 'Copy'}
                        </GlassButton>
                    </div>
                </>
            )}
        </GlassCard>
    );
};

export default SeoFilesPanel;
